<template>
  <CustomModal
    :is-open="isOpen"
    @close="close"
    content-class="bg-white dark:bg-zinc-900 p-0"
  >
    <div class="w-full max-w-md">
      <div class="p-2">
        <p class="text-md font-semibold text-black dark:text-white">Favorites</p>
      </div>
      <div class="px-4 py-2 overflow-y-auto">
        <ion-list>
          <ion-reorder-group :disabled="false" @ionItemReorder="handleReorder($event)">
            <ion-item
              lines="none"
              v-for="element in sortedFeeds"
              :key="element.id"
              class="bg-transparent"
            >
              <ion-reorder slot="start">
                <ion-icon
                  :icon="reorderTwoOutline"
                  class="text-gray-500 dark:text-gray-400"
                ></ion-icon>
              </ion-reorder>
              <div class="flex items-center w-full">
                <div class="flex items-center flex-1">
                  <span class="text-black dark:text-white ml-2">{{ element.name }}</span>
                </div>
                <div class="toggle-switch">
                  <input
                    type="checkbox"
                    :id="'toggle-' + element.id"
                    v-model="element.enabled"
                    @change="updateFeedPreferences"
                    :disabled="element.enabled && numberOfEnabledFeeds === 1"
                    class="toggle-input"
                  />
                  <label :for="'toggle-' + element.id" class="toggle-label"></label>
                </div>
              </div>
            </ion-item>
          </ion-reorder-group>
        </ion-list>
      </div>
    </div>
  </CustomModal>
</template>

<script setup>
import { computed } from "vue";
import CustomModal from "@/components/CustomModal.vue";
import { IonIcon, IonItem, IonList, IonReorder, IonReorderGroup } from "@ionic/vue";
import { reorderTwoOutline } from "ionicons/icons";
import { useGlobalStore } from "@/stores/global";

const globalStore = useGlobalStore();

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  feeds: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(["close", "update:feeds", "feedsUpdated"]);

// Sort feeds based on order property
const sortedFeeds = computed(() => {
  return [...props.feeds].sort((a, b) => a.order - b.order);
});

const handleReorder = (event) => {
  console.log("Dragged from index", event.detail.from, "to", event.detail.to);

  // Create a copy of the sorted feeds
  const items = [...sortedFeeds.value];

  // Save the previous default feed id (first item before reordering)
  const previousDefaultFeedId = items[0].id;

  // Get the item that was moved
  const movedItem = items[event.detail.from];

  // Remove the item from its original position
  items.splice(event.detail.from, 1);

  // Insert it at the new position
  items.splice(event.detail.to, 0, movedItem);

  // Update the order property for each item
  items.forEach((item, index) => {
    item.order = index;
  });

  // Complete the reorder animation
  event.detail.complete();

  // Save the updated feeds
  saveFeeds(items);

  // Check if the default feed (first item) has changed
  const newDefaultFeedId = items[0].id;
  if (previousDefaultFeedId !== newDefaultFeedId) {
    // Update the default feed in the global store and backend
    updateDefaultFeed(newDefaultFeedId);
  }
};

function updateDefaultFeed(feedId) {
  console.log(`Setting new default feed: ${feedId}`);

  // Update the default feed in the global store
  globalStore.settings.default_feed = feedId;

  // Update the setting in the backend
  globalStore.updateSettings("default_feed", feedId);
}

function close() {
  emit("close");
}

function saveFeeds(updatedFeeds) {
  localStorage.setItem("parlerFeedPreferences", JSON.stringify(updatedFeeds));
  emit("update:feeds", updatedFeeds);
  emit("feedsUpdated");
}

function updateFeedPreferences() {
  saveFeeds(props.feeds);
}

const numberOfEnabledFeeds = computed(() => {
  return props.feeds.filter((f) => f.enabled).length;
});
</script>

<style scoped>
/* Feed item styles */
.feed-item {
  padding: 12px 0;
}

/* Custom toggle switch styles */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 22px;
}

.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-label {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.toggle-label:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

.toggle-input:checked + .toggle-label {
  background-color: var(--primary-color);
}

.toggle-input:checked + .toggle-label:before {
  transform: translateX(24px);
}

/* Override ionic styles for transparent background */
:deep(.item-native) {
  background: transparent !important;
  --background: transparent !important;
}

:deep(.item-inner) {
  border-color: transparent !important;
}

:deep(.reorder-icon) {
  color: var(--ion-color-medium);
}
</style>
